import { ref, computed } from 'vue';
import { defineStore } from 'pinia';

/**
 * High school graduation date gate (ULC-3523).
 *
 * Learners under 18, or on a high school partner, must give a graduation month/year
 * (or choose not to) before using the portal. The backend decides who is asked;
 * this store holds that answer for the current learner.
 *
 * View-As: the admin sees the same modal the learner gets, but may close it.
 * Closing sets proxyDismissed, which only the View-As path ever sets.
 */
export const useHsGradDateStore = defineStore('hsGradDate', () => {
  // From GET /v2/api/learners/:learnerId/hs-graduation-date.
  const required = ref(false);
  // UNDER_18 | HIGH_SCHOOL_PARTNER | null — set whenever the learner is in scope,
  // even after they've answered (the profile uses it to show the field).
  const reason = ref(null);
  const graduationDate = ref(null);

  const proxyDismissed = ref(false);

  // True while the status request is in flight; App.vue holds the modal queue meanwhile.
  const pending = ref(false);

  const blocksPortal = computed(() => required.value && !proxyDismissed.value);

  function setStatus(status = {}) {
    required.value = Boolean(status?.required);
    reason.value = status?.reason ?? null;
    graduationDate.value = status?.graduationDate ?? null;
  }

  function setPending(value) {
    pending.value = Boolean(value);
  }

  function dismissForProxy() {
    proxyDismissed.value = true;
  }

  function clear() {
    required.value = false;
    reason.value = null;
    graduationDate.value = null;
    proxyDismissed.value = false;
    pending.value = false;
  }

  return {
    required,
    reason,
    graduationDate,
    proxyDismissed,
    pending,
    blocksPortal,
    setStatus,
    setPending,
    dismissForProxy,
    clear
  };
});
